import React, { useEffect, useState } from "react";
import TradingViewChart from "./TradingViewChart";
import LearningPanel from "./LearningPanel";
import ChartOverlayController from "./ChartOverlayController";
import TrendLineOverlayController from "./TrendLineOverlayController";
import useTradingAlerts from "../hooks/useTradingAlerts";

const ALERT_POPUP_LIFETIME_MS = 6000;

function formatAlertTitle(alert) {
  if (alert?.eventType === "market_in_zone") {
    return "Zone Active";
  }

  if (alert?.eventType === "spike_detected") {
    return "Spike Confirmed";
  }

  if (alert?.eventType === "zone_failed") {
    return "Zone Failed";
  }

  return "Zone Nearby";
}

export default function TradingDashboard({
  user,
  onLogout,
  onNavigateResults,
  onNavigateRadar,
  onNavigateAutoTrader,
  candles,
  setCandles,
  context,
  setContext,
  chartApi,
  setChartApi,
  analysis,
  setAnalysis,
  structure,
  setStructure,
  availableZones,
  setAvailableZones,
  availableOrderBlocks,
  setAvailableOrderBlocks,
  selectedZoneFilter,
  setSelectedZoneFilter,
  selectedOrderBlockFilter,
  setSelectedOrderBlockFilter,
  showAllZones,
  setShowAllZones,
  strategy,
  setStrategy,
}) {
  const {
    lastAlert,
    status,
    enablePushNotifications,
    dismissPushOnboarding,
  } = useTradingAlerts({
    symbol: context.symbol,
    timeframe: context.timeframe,
    user,
    currentPrice: candles[candles.length - 1]?.close,
    analysis,
  });
  const [popupAlerts, setPopupAlerts] = useState([]);
  const chartSessionKey = `${context.symbol}:${context.timeframe}`;

  useEffect(() => {
    return () => {
      setCandles([]);
      setAnalysis(null);
      setStructure(null);
      setAvailableZones([]);
      setAvailableOrderBlocks([]);
      setSelectedZoneFilter("ALL");
      setSelectedOrderBlockFilter("ALL");
      setShowAllZones(false);
      setChartApi(null);
    };
  }, [
    setAnalysis,
    setAvailableOrderBlocks,
    setAvailableZones,
    setCandles,
    setChartApi,
    setSelectedZoneFilter,
    setSelectedOrderBlockFilter,
    setShowAllZones,
    setStructure,
  ]);

  useEffect(() => {
    if (!lastAlert) {
      return undefined;
    }

    const popupId =
      lastAlert.id ||
      `${lastAlert.eventType || "event"}:${lastAlert.symbol || "symbol"}:${
        lastAlert.createdAt || Date.now()
      }`;
    const popupAlert = {
      ...lastAlert,
      popupId,
    };

    setPopupAlerts((current) => {
      const next = [popupAlert, ...current.filter((entry) => entry.popupId !== popupId)];
      return next.slice(0, 3);
    });

    const timeoutId = window.setTimeout(() => {
      setPopupAlerts((current) => current.filter((entry) => entry.popupId !== popupId));
    }, ALERT_POPUP_LIFETIME_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [lastAlert]);

  const dismissPopup = (popupId) => {
    setPopupAlerts((current) => current.filter((entry) => entry.popupId !== popupId));
  };

  return (
    <div className="app-shell">
      {status.showPushOnboarding && !status.pushEnabled ? (
        <div className="push-onboarding-modal-backdrop">
          <div className="push-onboarding-modal">
            <div className="push-onboarding-modal-kicker">Notifications</div>
            <h2 className="push-onboarding-modal-title">Enable push alerts now</h2>
            <p className="push-onboarding-modal-copy">
              Spike can notify you the moment price approaches, confirms, or fails a zone even if
              this tab is in the background.
            </p>
            <div className="push-onboarding-modal-actions">
              <button
                type="button"
                className="push-onboarding-modal-primary"
                onClick={enablePushNotifications}
              >
                Enable Push
              </button>
              <button
                type="button"
                className="push-onboarding-modal-secondary"
                onClick={dismissPushOnboarding}
              >
                Later
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {popupAlerts.length ? (
        <div className="alert-popup-stack" aria-live="assertive" aria-atomic="false">
          {popupAlerts.map((alert) => (
            <article
              key={alert.popupId}
              className={`alert-popup-card alert-popup-card-${alert.zone?.type || "neutral"}`}
            >
              <div className="alert-popup-top">
                <div>
                  <div className="alert-popup-kicker">Live Alert</div>
                  <strong className="alert-popup-title">{formatAlertTitle(alert)}</strong>
                </div>
                <button
                  type="button"
                  className="alert-popup-close"
                  onClick={() => dismissPopup(alert.popupId)}
                  aria-label="Dismiss alert notification"
                >
                  x
                </button>
              </div>
              <div className="alert-popup-symbol">
                {alert.symbol}
                {alert.zone?.type ? ` - ${alert.zone.type}` : ""}
              </div>
              <div className="alert-popup-copy">
                Price {Number(alert.price || 0).toFixed(2)}
                {alert.message ? ` | ${alert.message}` : ""}
                {alert.isTest ? " | Test alert" : ""}
              </div>
            </article>
          ))}
        </div>
      ) : null}

      <div className="dashboard-layout">
        <div className="dashboard-main">
          <TradingViewChart
            key={chartSessionKey}
            user={user}
            onLogout={onLogout}
            onNavigateResults={onNavigateResults}
            onNavigateRadar={onNavigateRadar}
            onNavigateAutoTrader={onNavigateAutoTrader}
            onCandleUpdate={setCandles}
            onContextUpdate={setContext}
            onAnalysisUpdate={setAnalysis}
            onStructureUpdate={setStructure}
            onChartReady={setChartApi}
            alertStatus={status}
            onEnableAlertDelivery={enablePushNotifications}
            availableZones={availableZones}
            availableOrderBlocks={availableOrderBlocks}
            selectedZoneId={selectedZoneFilter}
            selectedOrderBlockId={selectedOrderBlockFilter}
            onZoneFilterChange={setSelectedZoneFilter}
            onOrderBlockFilterChange={setSelectedOrderBlockFilter}
            showAllZones={showAllZones}
            onShowAllZonesChange={setShowAllZones}
            strategy={strategy}
            onStrategyChange={setStrategy}
          />

          {strategy === "SMART_ZONE" ? (
            <>
              <ChartOverlayController
                key={`overlay:${chartSessionKey}`}
                chartApi={chartApi}
                analysis={analysis}
                structure={structure}
                candles={candles}
                symbol={context.symbol}
                timeframe={context.timeframe}
                selectedZoneId={selectedZoneFilter}
                selectedOrderBlockId={selectedOrderBlockFilter}
                onZonesChange={setAvailableZones}
                onOrderBlocksChange={setAvailableOrderBlocks}
                onZoneFilterReset={setSelectedZoneFilter}
                onOrderBlockFilterReset={setSelectedOrderBlockFilter}
                showAllZones={showAllZones}
              />

              <LearningPanel
                key={`learning:${chartSessionKey}`}
                symbol={context.symbol}
                timeframe={context.timeframe}
                analysis={analysis}
              />
            </>
          ) : (
            <TrendLineOverlayController
              key={`trendline:${chartSessionKey}`}
              chartApi={chartApi}
              candles={candles}
              symbol={context.symbol}
              timeframe={context.timeframe}
            />
          )}
        </div>
      </div>
    </div>
  );
}
